import find from 'lodash/find'
import fromPairs from 'lodash/fromPairs'
import isUndefined from 'lodash/isUndefined'
import { createSelector } from 'reselect'

import { AUTOMATION_KEYS } from 'const/automations'
import {
    AutomationCapability,
    AutomationCapabilityId,
    AutomationTask,
    RootReduxState,
} from 'types'

import { selectDomainValue } from './ui'

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const selectCapabilities = (state: RootReduxState) =>
    selectDomainValue(state, ['campaignPage', 'automation', 'capabilities'])

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const selectTasks = (state: RootReduxState) =>
    selectDomainValue(state, ['campaignPage', 'automation', 'tasks'])

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const selectAutomationProfiles = (state: RootReduxState) =>
    selectDomainValue(state, [
        'campaignPage',
        'automation',
        'automationProfiles',
        'automationProfiles',
    ])

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function selectTask(
    tasks: AutomationTask[],
    capabilityId: AutomationCapabilityId
) {
    if (!tasks) {
        return undefined
    }
    const matchingTasks = tasks.filter(
        (task) => task.capability?.id === capabilityId
    )
    if (matchingTasks.length > 0) {
        const enabledTasks = matchingTasks.filter((task) => task.enabled)
        if (enabledTasks.length) {
            return enabledTasks[0]
        }
        return matchingTasks[0]
    }
    return undefined
}

/**
 * Returns map by capability Id of capability and task
 */
export const selectCapabilityTaskMapById = createSelector(
    selectCapabilities,
    selectTasks,
    (capabilities, tasks) =>
        fromPairs(
            capabilities.map((capability: AutomationCapability) => [
                capability.id,
                {
                    capability,
                    task: selectTask(tasks, capability.id),
                },
            ])
        )
)

/**
 * Checks automation tasks for current campaign to see if Dayparting is enabled
 */
export const selectIsDaypartingEnabled = createSelector(selectTasks, (tasks) =>
    tasks.some((task: AutomationTask) => {
        const {
            capability: { id: capabilityType },
            enabled,
        } = task
        return capabilityType === AUTOMATION_KEYS.time_parting && enabled
    })
)

/**
 * Checks automation tasks for current campaign to see if Dayparting is enabled
 */
export const selectEffectiveDaypartingMultipliers = createSelector(
    selectTasks,
    (tasks) => {
        const daypartingTask = find(tasks, (task) => {
            const capabilityType = task.capability?.id
            return capabilityType === AUTOMATION_KEYS.time_parting
        })
        if (isUndefined(daypartingTask)) {
            return {}
        }

        const biddingProfile = daypartingTask.automation_profile
        return {
            hourly_multiplier: biddingProfile?.params?.hourly_multiplier,
            daily_multiplier: biddingProfile?.params?.daily_multiplier,
        }
    }
)
